import { OutboundLink } from "./Map";
import { FULL_LOSS_COLOR, PARTIAL_LOSS_COLOR } from "./MapStyles";
import { LinkTo2019Pdf, LinkTo2022Pdf } from "./PdfLink";
import { TaxExemptions } from "./TaxExemptions";

const getBoroFromBbl = (bbl) => {
  const boroCode = Math.floor(bbl / 1000000000);
  return boroCode === 1
    ? ", MANHATTAN"
    : boroCode === 2
    ? ", BRONX"
    : boroCode === 3
    ? ", BROOKLYN"
    : boroCode === 4
    ? ", QUEENS"
    : boroCode === 5
    ? ", STATEN ISLAND"
    : "";
};

export const getBarColor = (selectedBuilding) =>
  selectedBuilding && selectedBuilding.u22 === 0
    ? FULL_LOSS_COLOR
    : PARTIAL_LOSS_COLOR;

export const BuildingDetail = ({ selectedBuilding, referrer }) => {
  if (!selectedBuilding) {
    return <div className="building-detail" />;
  }
  const { bbl, u19, u20, u21, u22, a, n } = selectedBuilding;

  const maxUnits = Math.max(u19, u20, u21, u22);

  const isSingleUnitDrop = u19 - u22 === 1;

  const barColor = getBarColor(selectedBuilding);

  return (
    <div className="building-detail is-open">
      <p>
        {a}
        {getBoroFromBbl(bbl)}
      </p>

      {!!bbl ? (
        <>
          <svg viewBox="0 0 400 120" preserveAspectRatio="none">
            <g transform="scale(1,-1)">
              <rect
                x="0"
                y="-100"
                width="60"
                height={(u19 / maxUnits) * 99 + 1}
                fill={barColor}
              ></rect>
              <text
                x="70"
                y="50"
                transform="scale(1,-1)"
                color="black"
                textAnchor="middle"
              >
                {u19}
              </text>
              <text x="50" y="120" transform="scale(1,-1)" color="black">
                2019
              </text>
              <rect
                x="80"
                y="-100"
                width="60"
                height={(u20 / maxUnits) * 99 + 1}
                fill={barColor}
              ></rect>
              <text
                x="150"
                y="50"
                transform="scale(1,-1)"
                color="black"
                textAnchor="middle"
              >
                {u20}
              </text>
              <text x="130" y="120" transform="scale(1,-1)" color="black">
                2020
              </text>
              <rect
                x="160"
                y="-100"
                width="60"
                height={(u21 / maxUnits) * 99 + 1}
                fill={barColor}
              ></rect>
              <text
                x="230"
                y="50"
                transform="scale(1,-1)"
                color="black"
                textAnchor="middle"
              >
                {u21}
              </text>
              <text x="210" y="120" transform="scale(1,-1)" color="black">
                2021
              </text>
              <rect
                x="240"
                y="-100"
                width="60"
                height={(u22 / maxUnits) * 99 + 1}
                fill={barColor}
              ></rect>
              <text
                x="310"
                y="50"
                transform="scale(1,-1)"
                color="black"
                textAnchor="middle"
              >
                {u22}
              </text>
              <text x="290" y="120" transform="scale(1,-1)" color="black">
                2022
              </text>
            </g>
            <text x="-80" y="20" transform="rotate(-90)" color="black">
              RS units
            </text>
          </svg>
          <p className="chart-note">
            {u22 === 0 ? (
              <>
                City agencies reported <b>zero</b> rent-stabilized apartments
                here in 2022 but reported <b>{u19}</b> in 2019.
              </>
            ) : (
              <>
                City agencies reported <b>{u19 - u22}</b> fewer unit
                {isSingleUnitDrop || "s"} here in 2022 than they did in 2019.
              </>
            )}
          </p>
          <TaxExemptions bbl={bbl} referrer={referrer} />
          <p className="chart-note">
            Neighborhood: {n > 0 ? <b>{NtaNamesOrdered[n]}</b> : "Unknown"}
          </p>
          <div className="button-group">
            <OutboundLink
              to={`https://whoownswhat.justfix.org/bbl/${bbl}?utm_source=thecity`}
              className="button"
              referrer={referrer}
            >
              View Landlord on WhoOwnsWhat
            </OutboundLink>
          </div>
          <div className="button-group">
            <LinkTo2019Pdf bbl={bbl} referrer={referrer} />{" "}
            <LinkTo2022Pdf bbl={bbl} referrer={referrer} />
          </div>
          <p>What does this mean?</p>
          <p className="chart-note">
            This building isn't currently showing registered rent-stabilized
            units due to an expiring tax break, a co-op conversion
            {u22 === 0 && " or a delay in registering"} — or there's no
            explanation for why its owner has removed apartments from state
            registration.{" "}
            <OutboundLink
              to="https://www.thecity.nyc/housing/2022/12/23/23523439/rent-regulated-apartments-registration-missing"
              referrer={referrer}
            >
              Learn more
            </OutboundLink>
          </p>

          <p>Is this your building?</p>
          <p className="chart-note">
            Your apartment may be rent-stabilized. Find out more in our{" "}
            <OutboundLink
              to="https://www.thecity.nyc/2022/12/23/23523675/is-your-apartment-rent-stabilized-how-to-get-rent-history"
              referrer={referrer}
            >
              explainer on requesting your rent history.
            </OutboundLink>
          </p>
          <p className="chart-note">
            If you spot an error, please let us know at{" "}
            <OutboundLink to="mailto:data@thecity.nyc" referrer={referrer}>
              data@thecity.nyc
            </OutboundLink>
            .
          </p>
          <div className="divider" />
          <p className="chart-note source-content">
            Source: Rent-stabilized unit estimates from NYC Dept. of Finance tax
            bills via{" "}
            <OutboundLink to="https://www.justfix.org/en/" referrer={referrer}>
              JustFix
            </OutboundLink>
            's{" "}
            <OutboundLink
              to="https://github.com/JustFixNYC/nyc-doffer"
              referrer={referrer}
            >
              NYC Doffer
            </OutboundLink>{" "}
            tool. Tax exemptions from{" "}
            <OutboundLink
              to="https://furmancenter.org/coredata/userguide/data-downloads"
              referrer={referrer}
            >
              NYU Furman Center’s CoreData.nyc
            </OutboundLink>
            . Neighborhood boundaries from{" "}
            <OutboundLink
              to="https://data.cityofnewyork.us/City-Government/NTA-map/d3qk-pfyz"
              referrer={referrer}
            >
              NYC Open Data
            </OutboundLink>
            .
          </p>
          <p className="chart-note source-content">
            Note: property tax bills reflect information from the preceding
            fiscal year.
          </p>
          <p className="chart-note source-content">
            <OutboundLink
              to="https://www.thecity.nyc/legal/privacy-notice"
              referrer={referrer}
            >
              Privacy Notice
            </OutboundLink>
          </p>
        </>
      ) : (
        <>
          <p>
            This building may be rent-stabilized, but it had <b>no reduction</b>{" "}
            in registered apartments after 2019.
          </p>
        </>
      )}
    </div>
  );
};

/**
 * This array lists out the names of NTAs by the order of their ID code.
 * For example, the NTA with id code `14` is the 14th element if this array,
 * and can be accessed by calling `NtaNamesOrdered[13]`.
 */
const NtaNamesOrdered = [
  "Midwood",
  "Bedford",
  "Fordham South",
  "Borough Park",
  "Rugby-Remsen Village",
  "East Flushing",
  "Woodhaven",
  "Madison",
  "Auburndale",
  "Williamsbridge-Olinville",
  "Murray Hill",
  "East Elmhurst",
  "Brownsville",
  "East New York (Pennsylvania Ave)",
  "Kensington-Ocean Parkway",
  "Parkchester",
  "Erasmus",
  "Cambria Heights",
  "East Flatbush-Farragut",
  "Ocean Parkway South",
  "Starrett City",
  "Morrisania-Melrose",
  "Elmhurst",
  "East Village",
  "Glen Oaks-Floral Park-New Hyde Park",
  "Longwood",
  "Yorkville",
  "Upper East Side-Carnegie Hill",
  "Windsor Terrace",
  "Hammels-Arverne-Edgemere",
  "Rikers Island",
  "Hunts Point",
  "Jackson Heights",
  "Bath Beach",
  "Old Town-Dongan Hills-South Beach",
  "Flatbush",
  "Melrose South-Mott Haven North",
  "Ocean Hill",
  "Morningside Heights",
  "Soundview-Bruckner",
  "Allerton-Pelham Gardens",
  "Jamaica Estates-Holliswood",
  "Hollis",
  "Flatlands",
  "East New York",
  "Kingsbridge Heights",
  "Springfield Gardens North",
  "Canarsie",
  "Norwood",
  "Manhattanville",
  "West Village",
  "Grasmere-Arrochar-Ft. Wadsworth",
  "Queens Village",
  "Chinatown",
  "Pelham Bay-Country Club-City Island",
  "Woodlawn-Wakefield",
  "Old Astoria",
  "Astoria",
  "Stuyvesant Town-Cooper Village",
  "Dyker Heights",
  "Bensonhurst West",
  "West New Brighton-New Brighton-St. George",
  "New Brighton-Silver Lake",
  "Westerleigh",
  "University Heights-Morris Heights",
  "Bayside-Bayside Hills",
  "Crown Heights North",
  "East Concourse-Concourse Village",
  "North Corona",
  "Cypress Hills-City Line",
  "Kew Gardens Hills",
  "Pomonok-Flushing Heights-Hillcrest",
  "North Side-South Side",
  "Lower East Side",
  "Greenpoint",
  "Spuyten Duyvil-Kingsbridge",
  "Sunset Park East",
  "Marble Hill-Inwood",
  "Homecrest",
  "Washington Heights North",
  "Steinway",
  "Mott Haven-Port Morris",
  "West Brighton",
  "Central Harlem North-Polo Grounds",
  "Queensbridge-Ravenswood-Long Island City",
  "New Dorp-Midland Beach",
  "Van Cortlandt Village",
  "Co-op City",
  "Bay Ridge",
  "Sunset Park West",
  "Fort Greene",
  "SoHo-TriBeCa-Civic Center-Little Italy",
  "Battery Park City-Lower Manhattan",
  "Clinton",
  "Prospect Heights",
  "Baisley Park",
  "South Jamaica",
  "Ozone Park",
  "Georgetown-Marine Park-Bergen Beach-Mill Basin",
  "Brighton Beach",
  "Bensonhurst East",
  "West Farms-Bronx River",
  "Sheepshead Bay-Gerritsen Beach-Manhattan Beach",
  "Westchester-Unionport",
  "Oakwood-Oakwood Beach",
  "Grymes Hill-Clifton-Fox Hills",
  "Park Slope-Gowanus",
  "Stapleton-Rosebank",
  "Fresh Meadows-Utopia",
  "Bellerose",
  "Stuyvesant Heights",
  "East Williamsburg",
  "Ridgewood",
  "East Harlem South",
  "Rego Park",
  "East Harlem North",
  "Bushwick North",
  "Bushwick South",
  "Central Harlem South",
  "College Point",
  "Midtown-Midtown South",
  "Glendale",
  "Charleston-Richmond Valley-Tottenville",
  "Parks, Cemeteries etc.-Staten Island",
  "New Springville-Bloomfield-Travis",
  "Todt Hill-Emerson Hill-Heartland Village-Lighthouse Hill",
  "South Ozone Park",
  "Lindenwood-Howard Beach",
  "Prospect Lefferts Gardens-Wingate",
  "Murray Hill-Kips Bay",
  "Mariner's Harbor-Arlington-Port Ivory-Graniteville",
  "Crown Heights South",
  "Brooklyn Heights-Cobble Hill",
  "Port Richmond",
  "Hunters Point-Sunnyside-West Maspeth",
  "Claremont-Bathgate",
  "Van Nest-Morris Park-Westchester Square",
  "Pelham Parkway",
  "Mount Hope",
  "Ft. Totten-Bay Terrace-Clearview",
  "Whitestone",
  "Turtle Bay-East Midtown",
  "Lenox Hill-Roosevelt Island",
  "Elmhurst-Maspeth",
  "Woodside",
  "St. Albans",
  "Laurelton",
  "Hamilton Heights",
  "Jamaica",
  "Richmond Hill",
  "Briarwood-Jamaica Hills",
  "Kew Gardens",
  "Middle Village",
  "Maspeth",
  "Upper West Side",
  "Lincoln Square",
  "Bronxdale",
  "Oakland Gardens",
  "Douglas Manor-Douglaston-Little Neck",
  "Annadale-Huguenot-Prince's Bay-Eltingville",
  "Great Kills",
  "Seagate-Coney Island",
  "Corona",
  "Schuylerville-Throgs Neck-Edgewater Park",
  "Gravesend",
  "East Tremont",
  "North Riverdale-Fieldston-Riverdale",
  "Bedford Park-Fordham North",
  "Belmont",
  "Eastchester-Edenwald-Baychester",
  "Breezy Point-Belle Harbor-Rockaway Park-Broad Channel",
  "Crotona Park East",
  "Rossville-Woodrow",
  "Arden Heights",
  "Far Rockaway-Bayswater",
  "Soundview-Castle Hill-Clason Point-Harding Park",
  "Parks, Cemeteries etc.-Bronx",
  "Carroll Gardens-Columbia Street-Red Hook",
  "Parks, Cemeteries etc.-Manhattan",
  "Rosedale",
  "Flushing",
  "Queensboro Hill",
  "Hudson Yards-Chelsea-Flatiron-Union Square",
  "Gramercy",
  "DUMBO-Vinegar Hill-Downtown Brooklyn-Boerum Hill",
  "Clinton Hill",
  "Williamsburg",
  "Parks, Cemeteries etc.-Brooklyn",
  "Washington Heights South",
  "Highbridge",
  "West Concourse",
  "Forest Hills",
  "Parks, Cemeteries etc.-Queens",
  "Springfield Gardens South-Brookville",
  "Airport",
];
